import { useGetMerchantQuery } from "Api/Queries/Merchants/useGetMerchantQuery";
import { ControlledCardsListSkeleton } from "Components/Shared/CardsList/ControlledCardsListSkeleton";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { FormSection } from "Components/Shared/FormSection";
import { AppProducts } from "Components/Shared/Merchant/Products/AppProducts/AppProducts";
import {
  isComgateProductCode,
  PRODUCTS_REQUIRING_FACILITY,
} from "Components/Shared/Merchant/Products/AppProducts/constants";
import type { ProductCode } from "Infrastructure/Api/Api";
import type { ReactNode } from "react";
import { useCallback } from "react";
import { Resources, useResource } from "Translations/Resources";

const COMPONENT_RESOURCES = Resources.Components.Merchant.AppProducts;

export const CurrentMerchantAppProducts = () => {
  const { data, isLoading, isError } = useGetMerchantQuery();
  const { t } = useResource();

  const hasFacilities = (data?.facilities.length ?? 0) > 0;

  const facility = data?.facilities[0];
  const hasFacilityWithPhotos =
    !!facility && facility.hasPhotoOutside && facility.hasPhotoInside;

  const getPrerequisites = useCallback(
    (code: ProductCode) => {
      if (!PRODUCTS_REQUIRING_FACILITY.includes(code)) {
        return undefined;
      }

      const items = [
        {
          text: t(
            Resources.Components.Merchant.AppProducts.Form.Prerequisites
              .FacilityAdded,
          ),
          isCompleted: hasFacilities,
        },
      ];

      if (isComgateProductCode(code)) {
        items.push({
          text: t(
            Resources.Components.Merchant.AppProducts.Form.Prerequisites
              .FacilityPhotos,
          ),
          isCompleted: hasFacilityWithPhotos,
        });
      }

      return items;
    },
    [hasFacilities, hasFacilityWithPhotos, t],
  );

  if (isLoading) {
    return (
      <Layout>
        <ControlledCardsListSkeleton />
      </Layout>
    );
  }

  if (!data || isError) {
    return (
      <Layout>
        <ErrorText>{t(COMPONENT_RESOURCES.LoadingError)}</ErrorText>
      </Layout>
    );
  }

  return (
    <AppProducts
      merchantPublicID={data.publicID}
      variant="activatable"
      getPrerequisites={getPrerequisites}
    />
  );
};

const Layout = ({ children }: { children: ReactNode }) => {
  const { t } = useResource();

  return (
    <FormSection legend={t(COMPONENT_RESOURCES.Title)}>{children}</FormSection>
  );
};
